import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Button, Space, Row, Spinner } from "@masterteam/mt-design-react";

import Table from "../NewTable";
import DeleteModal from "../DeleteModal";
import StakeholderForm from "../AddNewInitiative/Stakeholders/StakeholderForm";

import api from "../../providers/api";
import useIsAr from "../../Hooks/useIsAr";
import { addToast, getLookupName } from "../../helpers";

import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete.svg";

import "./style.scss";

const initialModalState = {
  isOpen: false,
  data: null,
};

const InitiativeStakeholders = ({ data }) => {
  const intl = useIntl();
  const [isAr] = useIsAr();

  const [stakeholders, setStakeholders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [formModal, setFormModal] = useState(initialModalState);
  const [lists, setLists] = useState({
    users: [],
  });

  const [StakeholderClassification, currentUser] = useSelector(
    ({ theme, user }) => [theme.StakeholderClassification.item.Items, user.user]
  );

  const isReadOnly = currentUser?.UserName !== data?.ManagerModel?.UserName;

  const getInitiativeStakeholders = async () => {
    setLoading(true);
    try {
      const res = await api.initiatives.getStakeholdersById(data.ID);
      setStakeholders(res.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getUsers = () => {
    api.user
      .getAll()
      .then((res) => {
        const { StatusCode, Data } = res;
        if (StatusCode === "Success") {
          setLists({ users: Data });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      });
  };

  const handleDelete = (id) => {
    setDeleteLoading(true);
    api.initiatives
      .deleteStakeholder(id)
      .then((res) => {
        const { StatusCode, StatusMessage } = res;
        if (StatusCode === "Success") {
          getInitiativeStakeholders();
          setDeleteModal(initialModalState);
          addToast.success("toast.successDelete");
        } else if (StatusCode === "fail") {
          addToast.error(StatusMessage, {
            toastId: "get-users-error",
          });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  useEffect(() => {
    getInitiativeStakeholders();
    getUsers();
  }, []);

  const columns = [
    {
      key: "Name",
      cell: (item) => item?.StakeholderName,
      header: intl.formatMessage({
        id: "initiative.stakeholder-name",
      }),
      width: "50%",
    },
    {
      key: "Classification",
      cell: (item) =>
        getLookupName(
          StakeholderClassification,
          `${item?.Classification}`,
          isAr
        ),
      header: intl.formatMessage({
        id: "initiative.classification",
      }),
      width: "50%",
    },
  ];

  const actions = [
    {
      key: "edit",
      variant: "primary",
      Icon: EditIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.update" }),
      onClick: (item) => {
        setFormModal({ isOpen: true, data: item });
      },
    },
    {
      key: "delete",
      variant: "error",
      Icon: DeleteIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.delete" }),
      onClick: (item) => {
        setDeleteModal({ isOpen: true, data: item.ID });
      },
    },
  ];

  return (
    <Space className="initiative-stakeholders" direction="vertical">
      <Row align="center" justify="between">
        <div className="title-wrapper">
          {intl.formatMessage({
            id: "initiative.stakeholders",
          })}
        </div>

        {!isReadOnly && (
          <Button
            shape="round"
            onClick={() => setFormModal({ isOpen: true, data: null })}
          >
            {intl.formatMessage({ id: "common.add" })}
          </Button>
        )}
      </Row>

      {loading ? (
        <Row justify="center">
          <Spinner size="large" />
        </Row>
      ) : (
        <Table
          data={stakeholders}
          columns={columns}
          actions={isReadOnly ? [] : actions}
          headerHasBackgroundColor
        />
      )}

      {formModal.isOpen && (
        <StakeholderForm
          data={formModal.data}
          stakeholders={stakeholders}
          setStakeholders={setStakeholders}
          closeModal={() => setFormModal(initialModalState)}
          lists={lists}
          initiativeData={data}
          getData={getInitiativeStakeholders}
        />
      )}

      {deleteModal.isOpen ? (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          title={intl.formatMessage({
            id: "initiative.stakeholder",
          })}
          closeModal={() => setDeleteModal(initialModalState)}
          handleDelete={() => handleDelete(deleteModal.data)}
          ButtonStatus={deleteLoading}
        />
      ) : null}
    </Space>
  );
};

export default InitiativeStakeholders;
